import { isEmpty, toString } from 'lodash';
import { useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { AuthDataProvider } from 'src/components/authProvider';
import { ECalendarViewsId } from 'src/components/Calendar/Calendar.interface';
import {
  useGetDaypartsByLocationGroup,
  useGetLocation,
  useGetLocationActiveDays,
  useGetProjectConfig,
  useModal,
  useParamQuery,
} from 'src/hooks';
import { checkIsDefaultLocationGroup } from 'src/hooks/api/locations/helper';
import { useExcluseScheduleLocation } from 'src/hooks/api/schedules/scheduleLocation/useExcluseScheduleLocation';
import { useApiErrorNavigator } from 'src/hooks/navigation/useApiErrorNavigator';

export const useCalendarLocationPage = () => {
  const [isActiveDayVisible, setIsActiveDayVisible] = useState(false);
  const { locationCode = '' } = useParams();
  const { userRole } = useContext(AuthDataProvider);
  const publishModal = useModal();
  const { data: projectConfig } = useGetProjectConfig();

  const { getParamsValue, onChangeParams } = useParamQuery();

  const locationGroupId = getParamsValue('groupID').join();
  const {
    data: activeDaypartOptions = [],
    isLoading: isLoadingActiveDayparts,
  } = useGetDaypartsByLocationGroup(
    [`location_group_id=${locationGroupId}`, 'valid_to.null=true'],
    Boolean(locationGroupId),
  );

  const {
    data: locationData,
    isLoading: isLocationLoading,
    error,
  } = useGetLocation(locationCode, userRole);

  const excludeScheduleLocation = useExcluseScheduleLocation();

  const { data: activeDays = [] } = useGetLocationActiveDays(
    locationCode,
    locationGroupId,
  );
  const [alertVisible, setAlertVisible] = useState(false);

  const isDefaultLocationGroup = checkIsDefaultLocationGroup(locationData);

  useApiErrorNavigator(error);
  useEffect(() => {
    setIsActiveDayVisible(activeDays.some((day) => day.active));
  }, [activeDays]);

  const onChangeLocationGroup = (
    event: React.SyntheticEvent,
    newValue: string,
  ) => {
    onChangeParams('groupID', newValue, true);
  };

  const filteredLocationGroups = locationData?.locationGroups?.filter(
    (group) => !group.is_default,
  );

  useEffect(() => {
    if (
      !isEmpty(filteredLocationGroups) &&
      !filteredLocationGroups?.find(
        (group) => group.id === Number(locationGroupId),
      )
    ) {
      onChangeParams('groupID', toString(filteredLocationGroups?.[0].id), true);
    }
  }, [
    locationData?.locationGroups,
    locationGroupId,
    onChangeParams,
    filteredLocationGroups,
  ]);

  const handleChangeViewType = (viewType: string) => {
    setIsActiveDayVisible(
      viewType !== ECalendarViewsId.resourceTimelineYear &&
        activeDays.some((day) => day.active),
    );
  };

  return {
    projectConfig,
    activeDaypartOptions,
    isLoadingActiveDayparts,
    isLocationLoading,
    onChangeLocationGroup,
    locationData,
    publishModal,
    locationGroup: locationData?.locationGroups?.find(
      (group) => group.id === Number(locationGroupId),
    ),
    isDefaultLocationGroup,
    locationGroups: filteredLocationGroups,
    excludeScheduleLocation,
    activeDays,
    isActiveDayVisible,
    handleChangeViewType,
    alertVisible,
    setAlertVisible,
  };
};
